import { useEffect, useRef, useState } from "react";
import { Logo } from "@/components/Logo";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Download, Loader2, CreditCard, ShieldCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });

function SignaturePad({ onCapture }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const inked = useRef(false);
  const last = useRef(null);
  useEffect(() => {
    const c = ref.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = Math.max(1, rect.width) * dpr;
    c.height = 150 * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#0d1420"; ctx.fillRect(0, 0, rect.width, 150);
    ctx.strokeStyle = "#2E7BE4"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round";
  }, []);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); const p = e.touches?.[0] || e; return { x: p.clientX - r.left, y: p.clientY - r.top }; };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p; inked.current = true;
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; if (inked.current) onCapture(ref.current.toDataURL("image/png")); };
  const clear = () => { const c = ref.current, ctx = c.getContext("2d"); const r = c.getBoundingClientRect(); ctx.fillStyle = "#0d1420"; ctx.fillRect(0, 0, r.width, 150); inked.current = false; onCapture(""); };
  return (
    <div>
      <canvas ref={ref} style={{ height: 150 }} data-testid="public-signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full touch-none rounded-xl border border-slate-700" />
      <button type="button" onClick={clear} className="mt-1 text-xs text-slate-400 hover:text-white">Clear</button>
    </div>
  );
}

export default function PublicQuote() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(false);
  const [signer, setSigner] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => pub.get(`/public/quotes/${token}`).then((r) => { setQ(r.data); setSigner((s) => s || r.data.signer_name || ""); }).catch(() => setErr(true));
  useEffect(() => { load(); }, [token]);

  // handle Stripe return
  useEffect(() => {
    const sid = params.get("paid");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await pub.get(`/public/quotes/${token}/pay-status/${sid}`);
        if (data.payment_status === "paid") { toast.success("Payment received. Thank you!"); load(); setParams({}); return; }
      } catch { /* keep polling */ }
      if (tries++ < 8) setTimeout(poll, 2000); else setParams({});
    };
    poll();
  }, [params]);

  const respond = async (action) => {
    if (action === "accept" && (!signer.trim() || !sig)) { toast.error("Type your name and sign to accept"); return; }
    setBusy(true);
    try {
      await pub.post(`/public/quotes/${token}/respond`, { action, signer_name: signer, signature: sig });
      toast.success(action === "accept" ? "Quote accepted - thank you!" : "Quote declined");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    setBusy(false);
  };

  const pay = async (milestoneId) => {
    setBusy(true);
    try {
      const { data } = await pub.post(`/public/quotes/${token}/pay`, { milestone_id: milestoneId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };

  if (err) return <div className="grid min-h-screen place-items-center bg-[#0B0F17] text-slate-300"><div className="text-center"><XCircle className="mx-auto h-10 w-10 text-rose-400" /><p className="mt-3">Quote not found or link expired.</p></div></div>;
  if (!q) return <div className="grid min-h-screen place-items-center bg-[#0B0F17]"><Loader2 className="h-8 w-8 animate-spin text-[#2E7BE4]" /></div>;

  const accepted = q.status === "accepted";
  const declined = q.status === "declined";

  return (
    <div className="min-h-screen bg-[#0B0F17] px-4 py-10 text-slate-200" data-testid="public-quote-page">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center gap-2">
          <Logo />
          <div>
            <div className="font-display text-lg font-bold text-white">{q.company_name}</div>
            <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Quote {q.number}</div>
          </div>
        </div>

        <div className="card-tac p-6">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-2xl font-bold text-white">Your quote</h1>
            {accepted && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /> Accepted</span>}
            {declined && <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-3 py-1 text-xs text-rose-300"><XCircle className="h-3.5 w-3.5" /> Declined</span>}
          </div>
          <p className="mt-1 text-sm text-slate-400">Prepared for {q.customer_name || "you"}</p>
          {q.summary && <p className="mt-3 text-sm text-slate-300">{q.summary}</p>}

          <div className="mt-5 divide-y divide-slate-800">
            {(q.line_items || []).map((li, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`pub-line-${i}`}>
                <span className="text-slate-200">{li.item} <span className="text-slate-500">×{li.qty}</span></span>
                <span className="font-mono-tac text-slate-300">${((li.qty || 0) * (li.unit_price || 0)).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 border-t border-slate-800 pt-4 text-sm">
            <div className="flex justify-between text-slate-400"><span>Subtotal</span><span>${(q.subtotal || 0).toFixed(2)}</span></div>
            <div className="flex justify-between text-slate-400"><span>Tax ({q.tax_rate || 0}%)</span><span>${(q.tax || 0).toFixed(2)}</span></div>
            <div className="flex justify-between pt-1 font-display text-xl font-bold text-white"><span>Total</span><span data-testid="pub-total">${(q.total || 0).toFixed(2)}</span></div>
          </div>

          {q.has_pdf && (
            <a href={`${API}/public/quotes/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="pub-pdf-link"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-white hover:border-cyan-500"><Download className="h-4 w-4 text-cyan-400" /> Download PDF</a>
          )}
          {q.notes && <p className="mt-4 rounded-lg border border-slate-800 bg-[#0d1420] p-3 text-xs text-slate-400">{q.notes}</p>}
        </div>

        {/* Accept / decline */}
        {!accepted && !declined && (
          <div className="mt-6 card-tac p-6" data-testid="approval-card">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-white"><ShieldCheck className="h-5 w-5 text-[#2E7BE4]" /> Approve &amp; e-sign</h2>
            <input placeholder="Type your full name" value={signer} onChange={(e) => setSigner(e.target.value)} data-testid="pub-signer-input"
              className="mt-3 w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white" />
            <div className="mt-3"><SignaturePad onCapture={setSig} /></div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => respond("accept")} disabled={busy} data-testid="pub-accept-btn" className="btn-amber flex-1 rounded-full py-3 text-sm disabled:opacity-50">Accept &amp; sign</button>
              <button onClick={() => respond("decline")} disabled={busy} data-testid="pub-decline-btn" className="rounded-full border border-slate-700 px-5 py-3 text-sm text-slate-300 hover:border-rose-500">Decline</button>
            </div>
          </div>
        )}

        {/* Payments */}
        {(q.payment_schedule || []).length > 0 && (
          <div className="mt-6 card-tac p-6" data-testid="pub-payments">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-white"><CreditCard className="h-5 w-5 text-[#2E7BE4]" /> Payments</h2>
            <div className="mt-3 space-y-2">
              {q.payment_schedule.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-[#0d1420] px-3 py-2.5 text-sm" data-testid={`pub-milestone-${m.id}`}>
                  <div><div className="text-slate-200">{m.label}</div><div className="font-mono-tac text-[10px] text-slate-500">${(m.amount || 0).toFixed(2)}</div></div>
                  {m.status === "paid"
                    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Paid</span>
                    : <button onClick={() => pay(m.id)} disabled={busy || (!accepted)} data-testid={`pub-pay-${m.id}`} className="rounded-full bg-[#2E7BE4] px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-40">Pay ${ (m.amount || 0).toFixed(0)}</button>}
                </div>
              ))}
            </div>
            {!accepted && <p className="mt-2 font-mono-tac text-[10px] text-slate-500">Accept the quote to enable payments.</p>}
            <p className="mt-2 font-mono-tac text-[10px] text-slate-600">Secure card payment via Stripe · test card 4242 4242 4242 4242</p>
          </div>
        )}
      </div>
    </div>
  );
}
